function validateStore(db, storeName) {
	return db.objectStoreNames.contains(storeName);
}

export function validateBeforeTransaction(db, storeName, reject) {
	if (!db) {
		reject("Queried before opening connection");
	}
	if (!validateStore(db, storeName)) {
		reject(`Store ${storeName} not found`);
	}
}

export function createTransaction(
	db,
	dbMode,
	currentStore,
	resolve,
	reject,
	abort
) {
	let tx = db.transaction(currentStore, dbMode);
	tx.onerror = reject;
	tx.oncomplete = resolve;
	tx.onabort = abort;
	return tx;
}

export async function getConnection(config) {
	return new Promise((resolve, reject) => {
		const idbInstance = typeof window !== "undefined" ? window.indexedDB : null;
		if (idbInstance) {
			const request = idbInstance.open(config.databaseName, config.version);

			request.onsuccess = () => {
				resolve(request.result);
			};

			request.onerror = (e) => {
				reject(e.target.error.name);
			};

			request.onblocked = (e) => {
				console.log("db onblocked", e);
                const db = e.target.result;
                db.close();
				reject(e);
			};

			request.onupgradeneeded = (e) => {
				const db = e.target.result;
				config.stores.forEach((s) => {
					if (!db.objectStoreNames.contains(s.name)) {
						const store = db.createObjectStore(s.name, s.id);
						s.indices.forEach((c) => {
							store.createIndex(c.name, c.keyPath, c.options);
						});
					}
				});
				db.close();
				resolve(undefined);
			};
		} else {
			reject("Failed to connect");
		}
	});
}

export function getActions(currentStore, config) {
	return {
		getByID(id) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readonly",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.get(id);
						request.onsuccess = (e) => {
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},
		getOneByIndex(keyPath, value) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readonly",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let index = objectStore.index(keyPath);
						let request = index.get(value);
						request.onsuccess = (e) => {
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},
		getManyByIndex(keyPath, value) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readonly",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let index = objectStore.index(keyPath);
						let request = index.getAll(value);
						request.onsuccess = (e) => {
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},
		getAll() {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readonly",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.getAll();
						request.onsuccess = (e) => {
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},

		add(value, key) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readwrite",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.add(value, key);
						request.onsuccess = (e) => {
							tx?.commit?.();
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},
		update(value, key) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readwrite",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.put(value, key);
						request.onsuccess = (e) => {
							tx?.commit?.();
							resolve(e.target.result);
						};
					})
					.catch(reject);
			});
		},

		deleteByID(id) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readwrite",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.delete(id);
						request.onsuccess = (e) => {
							tx?.commit?.();
							resolve(e);
						};
					})
					.catch(reject);
			});
		},
		deleteAll() {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readwrite",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						objectStore.clear();
						tx.oncomplete = (e) => {
							tx?.commit?.();
							resolve(e);
						};
					})
					.catch(reject);
			});
		},

		openCursor(cursorCallback, keyRange) {
			return new Promise((resolve, reject) => {
				getConnection(config)
					.then((db) => {
						validateBeforeTransaction(db, currentStore, reject);
						let tx = createTransaction(
							db,
							"readonly",
							currentStore,
							resolve,
							reject
						);
						let objectStore = tx.objectStore(currentStore);
						let request = objectStore.openCursor(keyRange);
						request.onsuccess = (e) => {
							cursorCallback(e);
							resolve();
						};
					})
					.catch(reject);
			});
		},
	};
}
